import { ReactNode, SyntheticEvent, useMemo } from 'react'
import { ModalTypes, useModal } from '@/components/Modal'
import { Log, LogStatus } from '@/types/logs'
import { OfficialReportRequestBody, OfficialReportStatus } from '@/types/officialReport'
import { Button } from '@/ui/Button'
import { MechanicalInformationRequestBody } from '@/types/mechanicalInformation'
import { getOfficialReportTypeByCode } from '../../../helpers/getOfficialReportTypeByCode'
import { useCheckPermissions } from '@/permissions/useCheckPermissions'
import { useAuthStore } from '@/store/auth-store'
import { Role } from '@/types/auth'
import { useCombinedLogsTableStateStore } from '@/modules/CombinedLogs/store'
import { checkIfMechanicalInformationIsSubmitted } from '@/modules/MechanicalInformation/helpers/checkIfMechanicalInformationIsSubmitted'
import { checkIsLogUnfinished } from '@/modules/CombinedLogs/helpers/checkIsLogUnfinished'

import styles from './LogAction.module.scss'

import CheckmarkIcon from '../../../assets/checkmark-icon.svg'

interface Props {
    log: Log
    isRowSelected?: boolean
    submitOfficialReportCallback?: (data: OfficialReportRequestBody) => void
    submitMechanicalInformationCallback?: (data: MechanicalInformationRequestBody) => void
    deleteOfficialReportCallback?: () => void
    deleteMechanicalInformationCallback?: () => void
    children?: ReactNode
    shouldDisplayCompleteRecord?: boolean
}

const WriteOfficialReportAction = ({ log, submitOfficialReportCallback, deleteOfficialReportCallback }: Props) => {
    const { openModal } = useModal()
    const hasPermission = useCheckPermissions()

    const reportType = getOfficialReportTypeByCode(log)
    const canManage = hasPermission('log', 'manageOfficialReport')
    const canView = hasPermission('log', 'viewOfficialReport')

    const couldWriteOrUpdateReport =
        reportType && ((log.report && OfficialReportStatus.Draft === log.report.status) || !log.report) && canManage
    const couldViewOrUpdateReport =
        reportType &&
        log.report &&
        ([OfficialReportStatus.Submitted, OfficialReportStatus.Sent].includes(log.report.status) || canView)
    const isReportSent = log.report?.status === OfficialReportStatus.Sent

    const handleOpenOfficialReportModal = (readOnly = false, event: SyntheticEvent) => {
        event.stopPropagation()

        openModal(ModalTypes.officialReport, {
            type: reportType,
            log,
            readOnly,
            submitCallback: submitOfficialReportCallback,
            deleteCallback: deleteOfficialReportCallback,
        })
    }

    return (
        <div className={styles.root}>
            {couldWriteOrUpdateReport && (
                <Button rounded size="small" onClick={(event) => handleOpenOfficialReportModal(false, event)}>
                    {!log.report ? 'Write report' : 'Update report'}
                </Button>
            )}
            {couldViewOrUpdateReport && (
                <Button
                    size="small"
                    variant="transparent"
                    className={styles.actionButton}
                    onClick={(event) => handleOpenOfficialReportModal(isReportSent || canView, event)}
                >
                    <CheckmarkIcon />
                    {isReportSent ? 'Report sent' : 'View report'}
                </Button>
            )}
        </div>
    )
}

const AddMechanicalInformationAction = ({
    log,
    submitMechanicalInformationCallback,
    deleteMechanicalInformationCallback,
}: Props) => {
    const { openModal } = useModal()

    const readOnly = !log.codingPeriodInfo?.permissions.includes('Update') && !checkIsLogUnfinished(log)
    const couldWriteOrUpdateMechanicalInformation =
        !log.mechanicalInfo || (log.mechanicalInfo && !checkIfMechanicalInformationIsSubmitted(log.mechanicalInfo))

    const handleOpenMechanicalInformationModal = (readOnly: boolean, event: SyntheticEvent) => {
        event.stopPropagation()

        if (readOnly && !log.mechanicalInfo) {
            return
        }

        openModal(ModalTypes.mechanicalInformation, {
            log,
            readOnly,
            submitCallback: submitMechanicalInformationCallback,
            deleteCallback: deleteMechanicalInformationCallback,
        })
    }

    return (
        <div className={styles.root}>
            {couldWriteOrUpdateMechanicalInformation ? (
                <Button rounded size="small" onClick={(event) => handleOpenMechanicalInformationModal(readOnly, event)}>
                    {!log.mechanicalInfo ? 'Add info' : 'Update info'}
                </Button>
            ) : (
                <Button
                    size="small"
                    variant="transparent"
                    className={styles.actionButton}
                    onClick={(event) => handleOpenMechanicalInformationModal(readOnly, event)}
                >
                    <CheckmarkIcon />
                    View info
                </Button>
            )}
        </div>
    )
}

const CompletedAction = ({ log, shouldDisplayCompleteRecord = true }: Props) => {
    const isIncompleteDisplayed = log.status === LogStatus.Incomplete && shouldDisplayCompleteRecord

    if (!isIncompleteDisplayed) {
        return null
    }

    return (
        <Button rounded size="small" className={styles.completeRecordButton}>
            Complete record
        </Button>
    )
}

export const LogAction = (props: Props) => {
    const { log, isRowSelected: _isRowSelected = false, shouldDisplayCompleteRecord = true, children = null } = props
    const { role } = useAuthStore()
    const { multipleSelection } = useCombinedLogsTableStateStore()
    const hasPermission = useCheckPermissions()
    const hasPermissionToManageInfo = hasPermission('log', 'manageMechanicalInformation')
    const hasPermissionToViewInfo = hasPermission('log', 'viewMechanicalInformation')
    const hasPermissionToManageOfficialReport = hasPermission('log', 'manageOfficialReport')
    const hasPermissionToViewOfficialReport = hasPermission('log', 'viewOfficialReport')
    const isRowSelected = multipleSelection.find((item) => item.id === log.id) || _isRowSelected
    const isOfficialReportActionDisplayed =
        (hasPermissionToManageOfficialReport && (log.codeTypeReasonInfo?.isOfficialReportRequired || log.report)) ||
        (hasPermissionToViewOfficialReport && log.report)
    const isMechanicalInfoActionDisplayed =
        (hasPermissionToManageInfo && (log.codeTypeReasonInfo?.isMechanicalInfoRequired || log.mechanicalInfo)) ||
        (hasPermissionToViewInfo && log.mechanicalInfo)
    const isIncompleteDisplayed = log.status === LogStatus.Incomplete && shouldDisplayCompleteRecord

    const actions = useMemo(() => {
        const result = []

        if (isIncompleteDisplayed) {
            result.push(CompletedAction)
        }

        const isMechanicalInfoRequired =
            log.codingPeriodInfo?.permissions.includes('Update') &&
            (!log.mechanicalInfo ||
                (log.mechanicalInfo && !checkIfMechanicalInformationIsSubmitted(log.mechanicalInfo)))

        const isOfficialReportRequired =
            ((log.report && OfficialReportStatus.Draft === log.report.status) || !log.report) &&
            hasPermissionToManageOfficialReport

        if (isMechanicalInfoActionDisplayed && isMechanicalInfoRequired) {
            result.unshift(AddMechanicalInformationAction)
        }

        if (isOfficialReportActionDisplayed && isOfficialReportRequired) {
            result.unshift(WriteOfficialReportAction)
        }

        if (isOfficialReportActionDisplayed && !isOfficialReportRequired) {
            result.push(WriteOfficialReportAction)
        }

        if (isMechanicalInfoActionDisplayed && !isMechanicalInfoRequired) {
            result.unshift(AddMechanicalInformationAction)
        }

        return result
    }, [isIncompleteDisplayed, isMechanicalInfoActionDisplayed, isOfficialReportActionDisplayed, log])

    if (!role || role === Role.MileageClerk) {
        return null
    }

    if (!isIncompleteDisplayed && !isMechanicalInfoActionDisplayed && !isOfficialReportActionDisplayed) {
        return children || '-'
    }

    if (!isRowSelected) {
        const Component = actions[0]

        return Component && <Component {...props} />
    }

    return (
        <div className={styles.buttonsWrapper}>
            {actions.map((Component, index) => (
                <Component key={index} {...props} />
            ))}
        </div>
    )
}
